/**
 * BankImportCard — EIN konsolidierter Bank-Import (Aug-2026, User-Wunsch).
 *
 * Ersetzt die früher getrennten Importer je Bank/Ziel. Lädt eine beliebige
 * Bank-Liste (Santander / CA Auto Bank / Renault / Bank11, .xls/.xlsx/.csv),
 * erkennt Bank + Listentyp automatisch und verteilt jede Zeile selbst in
 * Leasing-Rücknahmen bzw. Finanzierungs-Ausläufe.
 *
 * Ablauf: Datei wählen → Vorschau (dry_run) mit erkanntem Typ + Zählungen →
 * Bestätigen (Commit). Bei Fehlerkennung kann der Typ manuell übersteuert werden.
 */
import { useState } from "react";
import { Upload, Loader2, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

function Stat({ label, value, color }) {
    return (
        <div className="border rounded-sm px-2 py-1.5 bg-gray-50">
            <div className={`text-lg font-black ${color}`}>{(value ?? 0).toLocaleString("de-DE")}</div>
            <div className="text-[9px] uppercase tracking-wider text-gray-500">{label}</div>
        </div>
    );
}

export function BankImportCard({ onDone }) {
    const [preview, setPreview] = useState(null);
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [force, setForce] = useState(""); // "" = Auto

    const send = async (f, dry, forceVal) => {
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const q = `dry_run=${dry}${forceVal ? `&force=${forceVal}` : ""}`;
            const { data } = await api.post(`/dealer/bank-import?${q}`, fd, {
                headers: { "Content-Type": "multipart/form-data" }, timeout: 180000,
            });
            if (dry) { setPreview(data); setFile(f); }
            else {
                const parts = [
                    data.leasing.imported || data.leasing.updated
                        ? `Leasing-Rücknahmen: ${data.leasing.imported} neu, ${data.leasing.updated} aktualisiert`
                        : null,
                    data.finance.imported || data.finance.updated
                        ? `Finanzierungs-Ausläufe: ${data.finance.imported} neu, ${data.finance.updated} aktualisiert`
                        : null,
                    data.leads_created ? `${data.leads_created} Lead(s) angelegt` : null,
                ].filter(Boolean).join(" · ");
                toast.success(`${data.bank || "Bank"} (${data.detected_label}) importiert — ${parts || "keine neuen Vorgänge"}`, { duration: 9000 });
                setPreview(null); setFile(null); setForce(""); onDone?.();
            }
        } catch (e) { toast.error(e?.response?.data?.detail || "Import fehlgeschlagen"); }
        finally { setBusy(false); }
    };

    const onFile = (e) => { const f = e.target.files?.[0]; if (f) send(f, true, force); e.target.value = ""; };
    const reRunWithForce = (val) => { setForce(val); if (file) send(file, true, val); };

    const p = preview;
    const totalLeasing = p ? (p.leasing.imported + p.leasing.updated) : 0;
    const totalFinance = p ? (p.finance.imported + p.finance.updated) : 0;

    return (
        <section className="border-2 border-indigo-200 rounded-sm bg-indigo-50/40" data-testid="bank-import-card">
            <div className="px-4 py-3 border-b border-indigo-200 flex items-center gap-2">
                <ArrowRightLeft className="w-4 h-4 text-indigo-600" />
                <h3 className="font-black text-sm text-[#111827]">Bank-Liste importieren</h3>
                <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-indigo-600 text-white ml-1">Auto-Erkennung</span>
            </div>
            <div className="p-4 space-y-3">
                <p className="text-[11px] text-gray-600">
                    <strong>Eine</strong> Import-Funktion für alle Banken: Santander, CA Auto Bank, Renault &amp; Bank11
                    (.xls/.xlsx/.csv). Bank und Listentyp werden automatisch erkannt; <strong>Leasing</strong>-Vorgänge landen
                    in den Leasing-Rücknahmen, <strong>Finanzierungen</strong> in den Finanzierungs-Ausläufen.
                </p>
                <label className="inline-flex items-center gap-1.5 text-xs font-bold bg-indigo-600 text-white rounded-sm px-3 py-2 cursor-pointer hover:bg-indigo-700" data-testid="bank-import-file-label">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Datei wählen &amp; Vorschau
                    <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onFile} data-testid="bank-import-file" />
                </label>

                {p && (
                    <div className="bg-white border rounded-sm p-3 text-xs space-y-3" data-testid="bank-import-preview">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="font-bold text-[#111827]">Erkannt:</span>
                            <span className="px-2 py-0.5 rounded-sm bg-[#111827] text-white font-bold" data-testid="bank-import-detected">
                                {p.bank && p.bank !== p.detected_label ? `${p.bank} · ${p.detected_label}` : (p.detected_label || p.bank || "?")}
                            </span>
                            <span className="text-gray-400">falsch?</span>
                            <select value={force} onChange={(e) => reRunWithForce(e.target.value)}
                                className="border rounded-sm px-2 py-1 text-[11px]" data-testid="bank-import-force">
                                <option value="">Auto-Erkennung</option>
                                <option value="santander_leasing">Santander Leasing-Rückgaben</option>
                                <option value="financing">Bank-Bestandsliste (Finanzierung/CA/Renault/Bank11)</option>
                            </select>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            <Stat label="Leasing → Rücknahmen" value={totalLeasing} color="text-amber-700" />
                            <Stat label="davon neu" value={p.leasing.imported} color="text-emerald-700" />
                            <Stat label="Finanzierung → Ausläufe" value={totalFinance} color="text-blue-700" />
                            <Stat label="davon neu" value={p.finance.imported} color="text-emerald-700" />
                        </div>
                        {(p.skipped?.cars || p.skipped?.foreign || p.skipped?.existing || p.skipped?.green || p.skipped?.red) ? (
                            <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-gray-500">
                                {p.skipped?.cars ? <span>{p.skipped.cars} PKW ignoriert</span> : null}
                                {p.skipped?.foreign ? <span>{p.skipped.foreign} Fremdfahrzeuge ignoriert</span> : null}
                                {p.skipped?.existing ? <span>{p.skipped.existing} bereits im System</span> : null}
                                {p.skipped?.green ? <span>{p.skipped.green} „grün" übersprungen</span> : null}
                                {p.skipped?.red ? <span>{p.skipped.red} „rot" übersprungen</span> : null}
                                {p.skipped?.old ? <span>{p.skipped.old} vor 2026 (Altbestand)</span> : null}
                            </div>
                        ) : null}
                        {(p.enriched_customers || p.marked_done) ? (
                            <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-gray-600" data-testid="import-extra-stats">
                                {p.marked_done ? <span>{p.marked_done} als erledigt markiert</span> : null}
                                {p.enriched_customers ? <span>{p.enriched_customers} Kundenakten aktualisiert</span> : null}
                            </div>
                        ) : null}
                        {(p.per_sheet || []).length > 1 && (
                            <div className="mt-1 border border-gray-200 rounded-sm overflow-hidden" data-testid="import-per-sheet">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-50 px-2 py-1">Pro Tab</div>
                                <table className="w-full text-[10px]">
                                    <thead className="text-gray-400">
                                        <tr><th className="text-left px-2 py-0.5">Tab</th><th className="px-1">neu</th><th className="px-1">akt.</th><th className="px-1">erledigt</th><th className="px-1">übersprungen</th></tr>
                                    </thead>
                                    <tbody>
                                        {p.per_sheet.map((s, i) => (
                                            <tr key={i} className="border-t border-gray-100" data-testid={`import-sheet-row-${i}`}>
                                                <td className="text-left px-2 py-0.5 text-gray-700">{s.sheet}</td>
                                                <td className="text-center text-emerald-700 font-bold">{s.imported}</td>
                                                <td className="text-center text-blue-700">{s.updated}</td>
                                                <td className="text-center text-gray-600">{s.done}</td>
                                                <td className="text-center text-gray-400">{(s.skipped_green || 0) + (s.skipped_red || 0) + (s.skipped_old || 0)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {(p.samples || []).length > 0 && (
                            <div className="text-[10px] text-gray-500">
                                z.B.: {p.samples.slice(0, 3).map((x) => `${x.typ}: ${x.name || "—"} · ${x.model || "—"}${x.end ? ` · Ende ${x.end}` : ""}`).join(" | ")}
                            </div>
                        )}
                        <button onClick={() => send(file, false, force)} disabled={busy}
                            className="mt-1 w-full bg-[#111827] text-white rounded-sm py-2 text-sm font-bold hover:bg-black disabled:opacity-60"
                            data-testid="bank-import-confirm">
                            {busy ? "Importiert…" : `Import bestätigen (${totalLeasing + totalFinance} Vorgänge)`}
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
}

export default BankImportCard;
